// The stand-in for the device database when it cannot start: an in-memory SQLite (sql.js) that
// answers the same run / query / execute calls as a @capacitor-community/sqlite connection, so every
// feature and the sync keep working for the rest of the session. Nothing it holds survives the app.

const toChanges = (db, { withLastId = false } = {}) => {
    const changes = { changes: db.getRowsModified() }
    if (withLastId) changes.lastId = db.exec('SELECT last_insert_rowid() AS id;')[0]?.values?.[0]?.[0] ?? 0
    return { changes }
}

export const wrapSqlJsDatabase = (db) => ({
    isDBOpen: async () => ({ result: true }),
    open: () => Promise.resolve(),
    execute: async (sql) => {
        db.exec(sql)
        return toChanges(db)
    },
    run: async (sql, params = []) => {
        db.run(sql, params)
        return toChanges(db, { withLastId: true })
    },
    query: async (sql, params = []) => {
        const statement = db.prepare(sql)
        try {
            statement.bind(params)
            const values = []
            while (statement.step()) values.push(statement.getAsObject())
            return { values }
        } finally {
            statement.free()
        }
    },
})

// The last resort when even sql.js cannot load: reads find nothing, writes keep nothing.
export const EMPTY_DATABASE = Object.freeze({
    isDBOpen: async () => ({ result: true }),
    open: () => Promise.resolve(),
    execute: async () => ({ changes: { changes: 0 } }),
    run: async () => ({ changes: { changes: 0, lastId: 0 } }),
    query: async () => ({ values: [] }),
})

export async function openMemoryDatabase() {
    try {
        const [{ default: initSqlJs }, { default: wasmUrl }] = await Promise.all([
            import('sql.js'),
            import('sql.js/dist/sql-wasm-browser.wasm?url'),
        ])
        const SQL = await initSqlJs(wasmUrl ? { locateFile: () => wasmUrl } : undefined)
        return { connection: wrapSqlJsDatabase(new SQL.Database()), kind: 'memory' }
    } catch (error) {
        console.warn('[SQLite] In-memory database unavailable, local data is not kept this session', error)
        return { connection: EMPTY_DATABASE, kind: 'empty' }
    }
}
